import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { uploadProductImagesThunk, deleteProductImageThunk } from "../../redux/Thunk/Product/ProductThunks";

/**
 * ProductImageUploader
 * Drag & drop image uploader for products
 * Supports multiple file uploads, reordering, setting cover image, and deletion.
 */
const ProductImageUploader = ({
  images = [],
  onChange,
  productId = null,
  maxImages = 10,
  className = "",
  compact = false,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [deletingUrls, setDeletingUrls] = useState({});

  const imageList = Array.isArray(images) ? images.filter((img) => typeof img === "string" && img.trim()) : [];

  const handleFiles = async (fileList) => {
    const rawFiles = Array.from(fileList || []);
    if (rawFiles.length === 0) return;

    // Validate remaining capacity
    const currentCount = imageList.length;
    if (currentCount + rawFiles.length > maxImages) {
      toast.error(`You can only upload up to ${maxImages} images (currently ${currentCount})`);
      return;
    }

    // Validate files: type & size
    const validFiles = [];
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    for (const file of rawFiles) {
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        toast.error(`"${file.name}" is not a supported format. Please upload JPG, PNG, or WEBP.`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" exceeds the 10MB size limit.`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(validFiles.map((f) => ({ name: f.name, size: f.size })));

    try {
      const resultAction = await dispatch(uploadProductImagesThunk(validFiles));
      if (uploadProductImagesThunk.fulfilled.match(resultAction)) {
        const newUrls = resultAction.payload || [];
        if (newUrls.length > 0) {
          const updated = [...imageList, ...newUrls];
          onChange?.(updated);
          toast.success(
            newUrls.length === 1
              ? "Image uploaded successfully"
              : `${newUrls.length} images uploaded successfully`
          );
        }
      } else {
        const err = resultAction.payload || "Failed to upload images";
        toast.error(typeof err === "string" ? err : "Failed to upload images");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload images. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target?.files) {
      handleFiles(e.target.files);
    }
  };

  const handleRemoveImage = async (urlIndex) => {
    const targetUrl = imageList[urlIndex];
    if (!targetUrl) return;

    setDeletingUrls((prev) => ({ ...prev, [targetUrl]: true }));

    try {
      dispatch(deleteProductImageThunk({ url: targetUrl, productId }));

      const updated = imageList.filter((_, idx) => idx !== urlIndex);
      onChange?.(updated);
      toast.success("Image removed");
    } catch (err) {
      console.error("Delete error:", err);
      const updated = imageList.filter((_, idx) => idx !== urlIndex);
      onChange?.(updated);
    } finally {
      setDeletingUrls((prev) => {
        const next = { ...prev };
        delete next[targetUrl];
        return next;
      });
    }
  };

  const handleSetCover = (urlIndex) => {
    if (urlIndex === 0) return;
    const item = imageList[urlIndex];
    const remaining = imageList.filter((_, idx) => idx !== urlIndex);
    const updated = [item, ...remaining];
    onChange?.(updated);
    toast.success("Cover image updated");
  };

  const handleMoveLeft = (urlIndex) => {
    if (urlIndex <= 0) return;
    const updated = [...imageList];
    const temp = updated[urlIndex - 1];
    updated[urlIndex - 1] = updated[urlIndex];
    updated[urlIndex] = temp;
    onChange?.(updated);
  };

  const handleMoveRight = (urlIndex) => {
    if (urlIndex >= imageList.length - 1) return;
    const updated = [...imageList];
    const temp = updated[urlIndex + 1];
    updated[urlIndex + 1] = updated[urlIndex];
    updated[urlIndex] = temp;
    onChange?.(updated);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/jpg,image/webp"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Dropzone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-[12px] transition-all cursor-pointer p-5 flex flex-col items-center justify-center text-center ${
          isDragging
            ? "border-emerald-500 bg-emerald-50/60 ring-4 ring-emerald-100 scale-[1.01]"
            : "border-primary-200 bg-gray-50/50 hover:bg-white hover:border-primary-400"
        } ${compact ? "py-4" : "py-6"}`}
      >
        <div className="w-12 h-12 rounded-xl bg-white border border-primary-200/80 flex items-center justify-center mb-2 shadow-2xs group-hover:scale-105 transition-transform">
          <svg className="w-6 h-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>

        <div className="space-y-1">
          <p className="text-[13px] font-bold text-secondry-800">
            <span className="text-primary-600 hover:underline">Click to browse</span> or drag and drop product photos
          </p>
          <p className="text-[11px] text-primary-400">
            JPG, PNG or WEBP up to 10MB each
          </p>
        </div>

        <span className="mt-2 text-[10px] font-semibold text-primary-600 bg-primary-50 border border-primary-200 px-2.5 py-0.5 rounded-full">
          {imageList.length} of {maxImages} uploaded
        </span>
      </div>

      {/* Upload Progress Shimmer Card */}
      {isUploading && (
        <div className="bg-emerald-50/70 border border-emerald-200 rounded-[10px] p-3 flex items-center justify-between animate-pulse">
          <div className="flex items-center gap-2.5">
            <svg className="w-4 h-4 text-emerald-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-[12px] font-semibold text-emerald-900">
              Uploading {uploadProgress.length} image{uploadProgress.length > 1 ? "s" : ""}...
            </span>
          </div>
          <span className="text-[11px] text-emerald-700 font-medium">Please wait</span>
        </div>
      )}

      {/* Uploaded Images Grid */}
      {imageList.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] font-bold text-secondry-800 uppercase tracking-wider flex items-center gap-1.5">
              <span>Attached Images</span>
              <span className="text-[11px] font-normal text-primary-500 normal-case">
                (First image is the cover)
              </span>
            </span>
            <span className="text-[11px] text-primary-400">
              Drag or use arrows to rearrange
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {imageList.map((url, idx) => {
              const isCover = idx === 0;
              const isDeleting = !!deletingUrls[url];

              return (
                <div
                  key={url + idx}
                  className={`group relative rounded-[10px] border-2 bg-white overflow-hidden shadow-2xs transition-all ${
                    isCover
                      ? "border-primary-600 ring-2 ring-primary-200"
                      : "border-primary-100 hover:border-primary-300"
                  } ${isDeleting ? "opacity-40 pointer-events-none" : ""}`}
                >
                  {/* Image Display */}
                  <div className="aspect-square w-full p-2 bg-gray-50/50 flex items-center justify-center">
                    <img
                      src={url}
                      alt={`Product asset ${idx + 1}`}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* Cover Badge */}
                  {isCover && (
                    <span className="absolute top-1.5 left-1.5 px-2 py-0.5 bg-emerald-600 text-white text-[9px] font-extrabold uppercase rounded shadow-xs tracking-wider z-10">
                      Cover
                    </span>
                  )}

                  {/* Position Badge (if not cover) */}
                  {!isCover && (
                    <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-secondry-800/70 text-white text-[9px] font-mono rounded shadow-xs z-10">
                      #{idx + 1}
                    </span>
                  )}

                  {/* Actions Bar (Overlay on hover) */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-1.5 z-20">
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(idx);
                        }}
                        className="p-1 bg-rose-600 hover:bg-rose-700 text-white rounded shadow-sm transition-colors cursor-pointer"
                        title="Delete image"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>

                    <div className="flex items-center justify-between gap-1">
                      {/* Move Left */}
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveLeft(idx);
                        }}
                        className="p-1 bg-white/80 hover:bg-white text-secondry-800 disabled:opacity-30 rounded shadow-xs transition-colors cursor-pointer"
                        title="Move Left"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      {/* Set as Cover */}
                      {!isCover && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetCover(idx);
                          }}
                          className="px-1.5 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[9px] font-bold rounded shadow-xs transition-colors cursor-pointer whitespace-nowrap"
                          title="Make this the cover image"
                        >
                          Make Cover
                        </button>
                      )}

                      {/* Move Right */}
                      <button
                        type="button"
                        disabled={idx === imageList.length - 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveRight(idx);
                        }}
                        className="p-1 bg-white/80 hover:bg-white text-secondry-800 disabled:opacity-30 rounded shadow-xs transition-colors cursor-pointer"
                        title="Move Right"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductImageUploader;
